import Vue from "vue";

export const MAX_SYNCHRONOUS_REPORT_DAYS = 31;
export const REPORT_PROCESSING_MESSAGE =
  "Até 31 dias: o relatório será gerado agora e aberto automaticamente em uma nova guia. Você pode continuar usando o sistema enquanto o relatório é processado.";
export const REPORT_WITHOUT_PERIOD_MESSAGE =
  "O relatório será gerado após a confirmação. Você pode continuar usando o sistema enquanto aguarda.\n\nAo finalizar, ele será aberto automaticamente em uma nova guia. Se o processamento for enviado para a fila, você receberá uma notificação.";
export const REPORT_GENERATED_SUCCESS_MESSAGE =
  "Seu relatório será gerado em alguns segundos.";
const REPORT_URL_EVENT_TIMEOUT = 30000;
const REPORT_REQUEST_ID = "__synchronousReportRequestId";
const PDF_REPORT_ENDPOINTS = new Set([
  "/relatorios/receitas",
  "/relatorios/despesas",
  "/relatorios/movimentacao-produto",
  "/relatorios/compras",
  "/relatorios/resumo-compras",
  "/relatorios/lista-preco",
  "/relatorios/resumo-vendas",
  "/relatorios/vendas",
  "/relatorios/historico-cliente",
  "/relatorios/clientes",
  "/relatorios/orcamentos",
  "/relatorios/movimento-caixa",
  "/relatorios/comissao",
  "/relatorios/total-by-ncm",
  "/relatorios/vendas-importadas",
  "/relatorios/os",
  "/relatorios/os-horarios",
  "/relatorios/cobrancas",
  "/relatorios/orcamentos-detalhado",
  "/relatorios/sintetico-vendas",
  "/relatorios/pis-cofins",
  "/relatorios/totalizacao-estoque",
  "/relatorios/fornecedor-estoque",
  "/relatorios/previsao-compra",
  "/relatorios/clientes-inativos",
  "/relatorios/aniversariantes",
  "/relatorios/pedidos",
  "/relatorios/nfse",
  "/relatorios/controle-lotes",
  "/relatorios/pedidos-resumo",
  "/relatorios/carregamento-pedidos",
  "/relatorios/totalizacao-clientes",
  "/relatorios/atividade-clientes",
  "/relatorios/os-simplificada",
  "/relatorios/contrato",
  "/relatorios/fechamento-caixa",
  "/relatorios/fechamento-caixa-usuarios",
  "/relatorios/disponibilidade-financeira",
  "/relatorios/vendas-by-produto",
  "/relatorios/itens-mais-vendidos",
]);
const IMMEDIATE_PDF_REPORT_ENDPOINTS = new Set([
  "/relatorios/clientes",
  "/relatorios/aniversariantes",
  "/relatorios/movimentacao-produto",
  "/relatorios/historico-cliente",
  "/relatorios/totalizacao-estoque",
  "/relatorios/fornecedor-estoque",
]);

let nextRequestId = 1;
const pendingRequests = new Map();

const parseReportDate = (value) => {
  if (!value) {
    return null;
  }

  const match = String(value)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return timestamp;
};

const getRequestPayload = (response) => {
  const payload = response?.data;

  if (!payload) {
    return {};
  }

  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (error) {
      return {};
    }
  }

  return payload;
};

const getPdfReportEndpoint = (config) => {
  const requestUrl = (config?.url || "").split("?")[0].replace(/\/$/, "");

  return Array.from(PDF_REPORT_ENDPOINTS).find((endpoint) =>
    requestUrl.endsWith(endpoint)
  );
};

export const getInclusiveReportDays = (startDate, endDate) => {
  const start = parseReportDate(startDate);
  const end = parseReportDate(endDate);

  if (start === null || end === null) {
    return null;
  }

  if (end < start) {
    return null;
  }

  return Math.floor((end - start) / 86400000) + 1;
};

export const isSynchronousReportPeriod = (startDate, endDate) => {
  const days = getInclusiveReportDays(startDate, endDate);

  return days !== null && days <= MAX_SYNCHRONOUS_REPORT_DAYS;
};

const clearPendingRequest = (requestId) => {
  const pendingRequest = pendingRequests.get(requestId);

  if (pendingRequest?.timeoutId) {
    clearTimeout(pendingRequest.timeoutId);
  }

  pendingRequests.delete(requestId);
};

export const beginSynchronousReportRequest = (config) => {
  const pdfReportEndpoint = getPdfReportEndpoint(config);

  if (!pdfReportEndpoint) {
    return config;
  }

  const payload = getRequestPayload(config);

  if (
    !IMMEDIATE_PDF_REPORT_ENDPOINTS.has(pdfReportEndpoint) &&
    !isSynchronousReportPeriod(payload.data_inicial, payload.data_final)
  ) {
    return config;
  }

  const requestId = nextRequestId++;
  config[REPORT_REQUEST_ID] = requestId;
  pendingRequests.set(requestId, { timeoutId: null });

  if (typeof Vue?.$toast?.success === "function") {
    Vue.$toast.success(REPORT_GENERATED_SUCCESS_MESSAGE);
  }

  return config;
};

export const completeSynchronousReportRequest = (response) => {
  const requestId = response?.config?.[REPORT_REQUEST_ID];
  const responsePayload = response?.data;
  const directResponseUrl =
    responsePayload?.queued === false &&
    typeof responsePayload?.data === "string"
      ? responsePayload.data
      : null;

  if (directResponseUrl && getPdfReportEndpoint(response?.config)) {
    if (requestId && !pendingRequests.has(requestId)) {
      return null;
    }

    if (requestId) {
      clearPendingRequest(requestId);
    }

    return directResponseUrl;
  }

  if (!requestId || !pendingRequests.has(requestId)) {
    return null;
  }

  if (responsePayload?.queued === true) {
    clearPendingRequest(requestId);
    return null;
  }

  const responseUrl = responsePayload?.url_report;

  if (responseUrl) {
    clearPendingRequest(requestId);
    return responseUrl;
  }

  const timeoutId = setTimeout(() => {
    clearPendingRequest(requestId);
  }, REPORT_URL_EVENT_TIMEOUT);

  pendingRequests.set(requestId, { timeoutId });

  return null;
};

export const failSynchronousReportRequest = (error) => {
  const requestId = error?.config?.[REPORT_REQUEST_ID];

  if (requestId) {
    clearPendingRequest(requestId);
  }
};

export const getSynchronousReportEventUrl = (message) => {
  const reportUrl = message?.message;
  const pendingRequest = pendingRequests.keys().next();

  if (!reportUrl || pendingRequest.done) {
    return null;
  }

  clearPendingRequest(pendingRequest.value);

  return reportUrl;
};

export const openReportUrl = (reportUrl) => {
  const reportWindow = window.open(reportUrl, "_blank");

  if (!reportWindow) {
    window.location.assign(reportUrl);
  }
};
